import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AlignedText } from '@actual-app/components/aligned-text';
import { Button } from '@actual-app/components/button';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import type { AccountEntity } from '@actual-app/core/types/models';

import { useMoveAccountMutation } from '#accounts';
import { isAccountFailedSync } from '#accounts/syncStatus';
import { useAccountBalances } from '#hooks/useAccountBalances';
import { useAccounts } from '#hooks/useAccounts';
import { useClosedAccounts } from '#hooks/useClosedAccounts';
import { useLocalPref } from '#hooks/useLocalPref';
import { useUpdatedAccounts } from '#hooks/useUpdatedAccounts';
import { useSelector } from '#redux';
import * as bindings from '#spreadsheet/bindings';

import { Account } from './Account';
import {
  getPortfolioReorderTarget,
  groupPortfolioAccounts,
  sumAccountBalances,
} from './accountPortfolio';
import type { PortfolioGroup } from './accountPortfolio';
import { PortfolioAmount } from './PortfolioAmount';
import { PortfolioGroupSelect } from './PortfolioGroupSelect';
import { PortfolioSummary } from './PortfolioSummary';
import { SecondaryItem } from './SecondaryItem';
import { usePortfolioLabels } from './usePortfolioLabels';

const portfolioTitleColors = {
  cash: theme.sidebarPortfolioCash,
  savings: theme.sidebarPortfolioSavings,
  investment: theme.sidebarPortfolioInvestment,
  property: theme.sidebarPortfolioProperty,
  debt: theme.sidebarPortfolioDebt,
  other: theme.sidebarPortfolioOther,
} satisfies Record<PortfolioGroup, string>;

export function Accounts() {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [isClassifying, setIsClassifying] = useState(false);
  const { data: accounts = [] } = useAccounts();
  const prefs = useSelector(state => state.prefs.synced);
  const groups = groupPortfolioAccounts(accounts, prefs);
  const balances = useAccountBalances(
    groups.flatMap(group => group.accounts.map(account => account.id)),
  );
  const groupLabels = usePortfolioLabels();
  const updatedAccounts = useUpdatedAccounts();
  const { data: closedAccounts = [] } = useClosedAccounts();
  const syncingAccountIds = useSelector(state => state.account.accountsSyncing);

  const getAccountPath = (account: AccountEntity) => `/accounts/${account.id}`;

  const [showClosedAccounts, setShowClosedAccountsPref] = useLocalPref(
    'ui.showClosedAccounts',
  );

  function onDragChange(drag: { state: string }) {
    setIsDragging(drag.state === 'start');
  }

  const moveAccount = useMoveAccountMutation();

  const makeDropPadding = (i: number) => {
    if (i === 0) {
      return {
        paddingTop: isDragging ? 15 : 0,
        marginTop: isDragging ? -15 : 0,
      };
    }
    return undefined;
  };

  async function onReorder(
    id: string,
    dropPos: 'top' | 'bottom' | null,
    targetId: string,
  ) {
    const targetIdToMove = getPortfolioReorderTarget(
      accounts,
      prefs,
      id,
      dropPos,
      targetId,
    );
    if (targetIdToMove === undefined) {
      return;
    }

    moveAccount.mutate({ id, targetId: targetIdToMove });
  }

  const onToggleClosedAccounts = () => {
    setShowClosedAccountsPref(!showClosedAccounts);
  };

  return (
    <View
      style={{
        flexGrow: 1,
        '@media screen and (max-height: 480px)': {
          minHeight: 'auto',
        },
      }}
    >
      <View
        style={{
          height: 1,
          backgroundColor: theme.sidebarItemBackgroundHover,
          marginTop: 15,
          flexShrink: 0,
        }}
      />

      <View style={{ overflow: 'auto' }}>
        <PortfolioSummary accounts={accounts} balances={balances} />
        <View
          style={{
            flexShrink: 0,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            margin: '0 14px',
            paddingTop: 12,
          }}
        >
          <View
            style={{
              color: theme.sidebarItemText,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            <Trans>자산 구성</Trans>
          </View>
          <Button
            variant="bare"
            aria-expanded={isClassifying}
            onClick={() => setIsClassifying(!isClassifying)}
            style={{
              color: theme.sidebarItemText,
              fontSize: 12,
              minHeight: 40,
            }}
          >
            {isClassifying ? t('분류 완료') : t('분류')}
          </Button>
        </View>

        {groups.map(group => (
          <View
            key={group.id}
            style={{ flexShrink: 0 }}
            data-testid={`sidebar-portfolio-group-${group.id}`}
          >
            <View
              style={{
                padding: '16px 14px 8px',
                color: theme.sidebarItemText,
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              <AlignedText
                left={
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <View
                      aria-hidden="true"
                      style={{
                        width: 3,
                        height: 14,
                        borderRadius: 2,
                        flexShrink: 0,
                        backgroundColor: portfolioTitleColors[group.id],
                      }}
                    />
                    {groupLabels[group.id]}
                  </View>
                }
                right={
                  <PortfolioAmount
                    value={getGroupDisplayBalance(
                      group.id,
                      sumAccountBalances(group.accounts, balances),
                    )}
                  />
                }
              />
            </View>
            {group.accounts.map((account, i) => (
              <View key={account.id}>
                <Account
                  name={account.name}
                  account={account}
                  connected={!!account.bank}
                  pending={syncingAccountIds.includes(account.id)}
                  failed={isAccountFailedSync(account)}
                  updated={updatedAccounts.includes(account.id)}
                  to={getAccountPath(account)}
                  query={bindings.accountBalance(account.id)}
                  onDragChange={onDragChange}
                  onDrop={onReorder}
                  outerStyle={makeDropPadding(i)}
                  dragGroup={group.id}
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    paddingLeft: 26,
                    paddingRight: 14,
                    paddingTop: 6,
                    paddingBottom: 6,
                  }}
                  displayBalance={
                    group.id === 'debt' ? (
                      <PortfolioAmount
                        value={getGroupDisplayBalance(
                          group.id,
                          balances[account.id] ?? null,
                        )}
                      />
                    ) : undefined
                  }
                />
                {isClassifying && <PortfolioGroupSelect account={account} />}
              </View>
            ))}
          </View>
        ))}

        {closedAccounts.length > 0 && (
          <SecondaryItem
            style={{ marginTop: 15 }}
            title={
              showClosedAccounts
                ? t('Closed accounts')
                : t('Closed accounts...')
            }
            onClick={onToggleClosedAccounts}
            bold
          />
        )}

        {showClosedAccounts &&
          closedAccounts.map(account => (
            <Account
              key={account.id}
              name={account.name}
              account={account}
              to={getAccountPath(account)}
              query={bindings.accountBalance(account.id)}
              onDragChange={onDragChange}
              onDrop={onReorder}
            />
          ))}
      </View>
    </View>
  );
}

function getGroupDisplayBalance(group: PortfolioGroup, balance: number | null) {
  return group === 'debt' && balance !== null ? -balance : balance;
}
